import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Box, Button, Paper, Typography, useTheme, Modal } from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import UploadedFileIcon from "../../../img/uploaded-file-icon.png";
import { REQUEST_OTP_NS } from "./i18n";
import CameraAltIcon from '@mui/icons-material/CameraAlt';

function DragDropFileUpload(props: {
  onFileUpload: (file: File | null) => void;
}) {
  const { onFileUpload } = props;
  const { palette } = useTheme();
  const { t } = useTranslation(REQUEST_OTP_NS);
  
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const baseActionButtonStyle = { py: 1, px: 0.5, height: 32 };

  const registerFileUpload = useCallback((file: File | undefined | null) => {
    if (file) {
      setUploadedFile(file);
      onFileUpload(file);
    }
  }, [setUploadedFile, onFileUpload]);

  const handleDragOver = useCallback((event: React.DragEvent<Element>) => {
    event.preventDefault();
    setDragOver(true);
  }, [setDragOver]);

  const handleDragLeave = useCallback((event: React.DragEvent<Element>) => {
    event.preventDefault();
    setDragOver(false);
  }, [setDragOver]);

  const handleDrop = useCallback((event: React.DragEvent<Element>) => {
    event.preventDefault();

    setDragOver(false);
    registerFileUpload(event.dataTransfer?.files[0]);
  }, [setDragOver, registerFileUpload]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    registerFileUpload(event.target.files?.[0])
  },  [registerFileUpload]);

  const handleTakePhoto = useCallback(() => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (videoRef.current && context) {
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => {
        if (blob) {
          const file = new File([blob], `${new Date().toISOString()}.jpg`, { type: 'image/jpeg' });
          registerFileUpload(file);
          const stream = videoRef.current?.srcObject as MediaStream;
          stream.getTracks().forEach(track => track.stop());
          setShowCamera(false);
        }
      }, 'image/jpeg');
    }
  }, [registerFileUpload]);

  const onWebcamClick = useCallback(() => {
    setShowCamera(true);
    navigator.mediaDevices.getUserMedia({ video: true })
      .then(stream => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      })
      .catch(error => console.error('Error accessing camera:', error));
  }, []);
  
  const handleCloseCamera = useCallback(() => {
    const stream = videoRef.current?.srcObject as MediaStream;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  }, []);


  const noFileContent = (
    <>
      <FileUploadOutlinedIcon
        sx={{
          fontSize: 20,
          transition: "0.2s",
          color: dragOver ? "#3AA4E6" : palette.text.placeholder,
          mb: 1,
        }}
      />
      <Typography
        sx={{
          transition: "0.2s",
          color: dragOver ? "#3AA4E6" : palette.text.placeholder,
        }}
      >
        {t("dropFileForm.uploadFile.dropFile")}
      </Typography>
    </>
  );

  const fileAddedContent = (
    <>
      <img src={UploadedFileIcon} alt="uploaded_file" />
      <Typography sx={{ mb: 1, mt: 2 }}>
        {uploadedFile?.name ?? null}
      </Typography>
      <label htmlFor="replace-file-input">
        <Typography
          sx={{
            width: "fit-content",
            cursor: "pointer",
            color: "#1D82C0",
          }}
        >
          {t("dropFileForm.uploadFile.replace")}
        </Typography>
      </label>
      <input
        id="replace-file-input"
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
    </>
  );

  const uploadBlockBorderStyle = dragOver ? "#3AA4E6" : "transparent";

  return (
    <>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -2 }}>
        <Button 
          onClick={onWebcamClick}
          sx={{
            ...baseActionButtonStyle,
            display: 'flex',
            alignItems: 'center',
            color: '#1D82C0',
            backgroundColor: 'inherit',
            zIndex: 2,
            position: 'relative',
            top: '-20px',
            marginTop: '8px',
          }}
        >
          <CameraAltIcon fontSize="small" sx={{ color: '#1D82C0' }} />
          <Typography variant="body2" sx={{ ml: 1 }}>Camera</Typography>
        </Button>
      </Box>
      
      <Paper
        variant="outlined"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          transition: "0.2s",
          border: uploadedFile ? "none" : "1px dashed",
          borderColor: uploadedFile ? "none" : uploadBlockBorderStyle,
          backgroundColor: dragOver
            ? "rgba(29, 130, 192, 0.12)"
            : palette.divider,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          padding: uploadedFile ? 2 : 5,
          zIndex: 1,
        }}
      >
       
        <input
          accept="image/*"
          style={{ display: "none" }}
          id="raised-button-file"
          multiple
          type="file"
          onChange={handleChange}
        />
        <label htmlFor="raised-button-file">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              cursor: uploadedFile ? "auto" : "pointer",
              p: uploadedFile ? 2 : 5,
            }}
          >
            {uploadedFile ? fileAddedContent : noFileContent}
          </Box>
        </label>
      </Paper>
    
      <Modal
        open={showCamera}
        onClose={handleCloseCamera}
        aria-labelledby="camera-modal"
        aria-describedby="camera-modal-description"
        disableAutoFocus
      >
        <Box 
          onClick={handleCloseCamera} 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <Box 
            onClick={(e) => e.stopPropagation()} 
            sx={{
              width: '50%',
              height: '60%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <video ref={videoRef} style={{ width: '100%', height: 'auto', objectFit: 'cover' }} />
            <Button 
              onClick={handleTakePhoto} 
              sx={{
                ...baseActionButtonStyle,
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'inherit',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
              }}
            >
              <CameraAltIcon fontSize="large" sx={{ color: palette.text.placeholder }} />
            </Button>
          </Box>
        </Box>
      </Modal>
  </>
  );
}

export default DragDropFileUpload;
